import React, { useState, useCallback } from 'react';
import {
    Box,
    Heading,
    Text,
    VStack,
    Button,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Spinner,
    useToast,
    Flex,
    Card,
    CardBody,
    Alert,
    AlertIcon,
    AlertDescription,
} from '@chakra-ui/react';
import { DownloadIcon, ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { FiDownload } from 'react-icons/fi';
import Navbar from '../../../components/Navbar';
import { FilterHarvestMonitoring } from '../../harvest/components/FilterHarvestMonitoring';
import { downloadBkmMuatPDF } from '../utils/bkmMuatPdfGenerator';
import dayjs from 'dayjs';
import rkhApiService from '../../rkh/services/rkhApiService';
import bkmMuatApiService from '../services/bkmMuatApiService';
import { BkmMuatRow, FilterValues } from '../types';

export const BkmMuatPage: React.FC = () => {
    const [data, setData] = useState<BkmMuatRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const toast = useToast();

    const [rkhBlocks, setRkhBlocks] = useState<any[]>([]);
    
    // Collapsible states
    const [expandedActivities, setExpandedActivities] = useState<Record<string, boolean>>({});
    const [expandedCostCenters, setExpandedCostCenters] = useState<Record<string, boolean>>({});

    const toggleActivity = (actName: string) => {
        setExpandedActivities(prev => ({
            ...prev,
            [actName]: prev[actName] === false ? true : false
        }));
    };

    const toggleCostCenter = (actName: string, ccName: string) => {
        const key = `${actName}-${ccName}`;
        setExpandedCostCenters(prev => ({
            ...prev,
            [key]: prev[key] === false ? true : false
        }));
    };

    // Default filters
    const [filters, setFilters] = useState<FilterValues>({
        current_date: dayjs().format('YYYYMMDD'),
        estate_id: 14, // default matching FilterHarvestMonitoring
        activity_code: 'all' // default to Semua Aktivitas
    });

    const fetchData = useCallback(async (activeFilters: FilterValues) => {
        if (!activeFilters.division_id) {
            setData([]);
            setRkhBlocks([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await bkmMuatApiService.getBkmMuatReport(activeFilters);

            if (response.status === 'success') {
                setData(response.data || []);
            } else {
                throw new Error(response.message || 'Failed to fetch BKM Muat data');
            }

            // Fetch RKH Summary for target values
            try {
                const dateYmd = activeFilters.current_date.includes('-')
                    ? activeFilters.current_date
                    : `${activeFilters.current_date.substring(0, 4)}-${activeFilters.current_date.substring(4, 6)}-${activeFilters.current_date.substring(6, 8)}`;
                
                const rkhRes = await rkhApiService.getRKHSummary({
                    date_from: dateYmd,
                    date_to: dateYmd,
                    company_id: activeFilters.company_id || 1,
                    estate_id: activeFilters.estate_id,
                    division_id: activeFilters.division_id
                });
                
                const blocksList: any[] = [];
                if (rkhRes && rkhRes.data) {
                    rkhRes.data.forEach((companyGroup: any) => {
                        if (companyGroup.estates) {
                            companyGroup.estates.forEach((estateGroup: any) => {
                                if (estateGroup.divisions) {
                                    estateGroup.divisions.forEach((divisionGroup: any) => {
                                        if (divisionGroup.blocks) {
                                            divisionGroup.blocks.forEach((block: any) => {
                                                blocksList.push(block);
                                            });
                                        }
                                    });
                                }
                            });
                        }
                    });
                }
                setRkhBlocks(blocksList);
            } catch (rkhErr) {
                console.error('Failed to load RKH summary targets:', rkhErr);
                setRkhBlocks([]);
            }
        } catch (err: any) {
            const msg = err.response?.data?.message || err.message || 'Error loading BKM Muat data';
            setError(msg);
            setData([]);
            setRkhBlocks([]);
            toast({
                title: 'Error Fetching Data',
                description: msg,
                status: 'error',
                duration: 5000,
                isClosable: true,
                position: 'top-right'
            });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    const handleFiltersChange = (newFilters: any) => {
        const updatedFilters = {
            current_date: newFilters.current_date,
            company_id: newFilters.company_id,
            estate_id: newFilters.estate_id,
            division_id: newFilters.division_id,
            activity_code: newFilters.activity_code
        };
        setFilters(updatedFilters);
        fetchData(updatedFilters);
    };

    const handleDownloadPDF = () => {
        if (data.length === 0) {
            toast({
                title: 'Download Gagal',
                description: 'Tidak ada data untuk diunduh',
                status: 'warning',
                duration: 3000,
                isClosable: true,
                position: 'top-right'
            });
            return;
        }

        downloadBkmMuatPDF(data, {
            posting_date: filters.current_date,
            division_name: data[0]?.division_name,
            estate_name: data[0]?.estate_name,
            rkh_number: data[0]?.rkh_number,
        }, rkhBlocks);
    };

    // Format numbers
    const formatDecimal = (num?: number | null) => {
        if (num === undefined || num === null) return '-';
        return new Intl.NumberFormat('id-ID', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(num);
    };

    const formatNumber = (num?: number | null) => {
        if (num === undefined || num === null) return '-';
        return new Intl.NumberFormat('id-ID', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(num);
    };

    // Grouping for tree view rendering on screen
    const groupedData: {
        [activity: string]: {
            [costCenter: string]: BkmMuatRow[]
        }
    } = {};

    data.forEach((row) => {
        const actName = row.cost_activity_name || '<Tanpa Aktivitas>';
        const ccName = row.cost_center_name || '<Tanpa Cost Center>';
        
        if (!groupedData[actName]) {
            groupedData[actName] = {};
        }
        if (!groupedData[actName][ccName]) {
            groupedData[actName][ccName] = [];
        }
        groupedData[actName][ccName].push(row);
    });

    return (
        <Box minH="100vh" bg="gray.50">
            <Navbar />

            <Box flex="1" p={{ base: 4, md: 6 }}>
                <VStack spacing={6} align="stretch">
                    <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap">
                        <VStack align="start" spacing={1}>
                            <Heading size="lg" color="gray.700">
                                Laporan BKM Muat
                            </Heading>
                        </VStack>
                    </Flex>

                    <FilterHarvestMonitoring
                        onFiltersChange={handleFiltersChange}
                        isLoading={loading}
                        isDivisionRequired={true}
                        showActivityFilter={false}
                        onDownloadPDF={handleDownloadPDF}
                        isDownloadDisabled={loading || data.length === 0}
                    />

                    {loading ? (
                        <Box bg="white" borderRadius="lg" p={10} border="1px" borderColor="gray.200" boxShadow="sm">
                            <Flex justifyContent="center" alignItems="center">
                                <Spinner size="xl" color="blue.500" />
                            </Flex>
                        </Box>
                    ) : error ? (
                        <Alert status="error" borderRadius="md">
                            <AlertIcon />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    ) : !filters.division_id ? (
                        <Box py={8} textAlign="center" color="gray.500">
                            Silakan pilih divisi terlebih dahulu.
                        </Box>
                    ) : data.length === 0 ? (
                        <Alert status="info" borderRadius="md">
                            <AlertIcon />
                            <AlertDescription>Tidak ada data untuk ditampilkan</AlertDescription>
                        </Alert>
                    ) : (
                        <Box bg="white" borderRadius="lg" overflow="hidden" border="1px" borderColor="gray.200" boxShadow="sm">
                            <Box overflowX="auto">
                                <Table variant="simple" size="sm">
                                    <Thead bg="#4A5A33">
                                        <Tr>
                                            <Th rowSpan={2} textAlign="left" color="white" borderColor="rgba(255,255,255,0.2)">KEGIATAN / LOKASI / PEKERJA</Th>
                                            <Th colSpan={2} textAlign="center" color="white" borderColor="rgba(255,255,255,0.2)">TANDAN</Th>
                                            <Th colSpan={2} textAlign="center" color="white" borderColor="rgba(255,255,255,0.2)">BERAT (KG)</Th>
                                            <Th rowSpan={2} textAlign="right" color="white" borderColor="rgba(255,255,255,0.2)">HK</Th>
                                            <Th rowSpan={2} textAlign="right" color="white" borderColor="rgba(255,255,255,0.2)">PREMI</Th>
                                        </Tr>
                                        <Tr>
                                            <Th textAlign="center" color="white" borderColor="rgba(255,255,255,0.2)">TARGET</Th>
                                            <Th textAlign="right" color="white" borderColor="rgba(255,255,255,0.2)">HASIL</Th>
                                            <Th textAlign="center" color="white" borderColor="rgba(255,255,255,0.2)">TARGET</Th>
                                            <Th textAlign="right" color="white" borderColor="rgba(255,255,255,0.2)">HASIL</Th>
                                        </Tr>
                                    </Thead>
                                     <Tbody>
                                         {Object.keys(groupedData).map((actName) => {
                                             // Calculate Activity Totals and Targets
                                             let actBunch = 0;
                                             let actWeight = 0;
                                             let actHk = 0;
                                             let actPremi = 0;
                                             let actTargetBunch = 0;
                                             let actTargetWeight = 0;
                                             
                                             const getBlockTarget = (ccName: string) => {
                                                 const cleanedCc = ccName.trim();
                                                 const block = rkhBlocks.find((b: any) => b.block_code?.trim() === cleanedCc);
                                                 return {
                                                     est_bunch: block ? block.est_bunch : 0,
                                                     est_weight: block ? block.est_weight : 0
                                                 };
                                             };

                                             Object.keys(groupedData[actName]).forEach((ccName) => {
                                                 groupedData[actName][ccName].forEach((loader) => {
                                                     actBunch += loader.sum_bunch_count || 0;
                                                     actWeight += loader.sum_real_weight || 0;
                                                     actHk += loader.sum_hk || 0;
                                                     actPremi += loader.sum_premi_amount || 0;
                                                 });
                                                 const ccTarget = getBlockTarget(ccName);
                                                 actTargetBunch += ccTarget.est_bunch;
                                                 actTargetWeight += ccTarget.est_weight;
                                             });

                                             const isActExpanded = expandedActivities[actName] !== false;

                                             return (
                                                 <React.Fragment key={actName}>
                                                     {/* Level 1: Cost Activity Row */}
                                                     <Tr 
                                                         bg="#e7efdf" 
                                                         fontWeight="bold"
                                                         onClick={() => toggleActivity(actName)}
                                                         cursor="pointer"
                                                         _hover={{ bg: "#dbe8d0" }}
                                                     >
                                                         <Td>
                                                             <Flex align="center" gap={2}>
                                                                 {isActExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                                                                 <Text>{actName}</Text>
                                                             </Flex>
                                                         </Td>
                                                         <Td textAlign="center">{actTargetBunch > 0 ? formatNumber(actTargetBunch) : '-'}</Td>
                                                         <Td textAlign="right">{formatNumber(actBunch)}</Td>
                                                         <Td textAlign="center">{actTargetWeight > 0 ? formatDecimal(actTargetWeight) : '-'}</Td>
                                                         <Td textAlign="right">{formatDecimal(actWeight)}</Td>
                                                         <Td textAlign="right">{formatDecimal(actHk)}</Td>
                                                         <Td textAlign="right">{formatDecimal(actPremi)}</Td>
                                                     </Tr>

                                                     {isActExpanded && Object.keys(groupedData[actName]).map((ccName) => {
                                                         // Calculate Cost Center Totals
                                                         let ccBunch = 0;
                                                         let ccWeight = 0;
                                                         let ccHk = 0;
                                                         let ccPremi = 0;

                                                         groupedData[actName][ccName].forEach((loader) => {
                                                             ccBunch += loader.sum_bunch_count || 0;
                                                             ccWeight += loader.sum_real_weight || 0;
                                                             ccHk += loader.sum_hk || 0;
                                                             ccPremi += loader.sum_premi_amount || 0;
                                                         });

                                                         const ccTarget = getBlockTarget(ccName);
                                                         const ccKey = `${actName}-${ccName}`;
                                                         const isCcExpanded = expandedCostCenters[ccKey] !== false;

                                                         return (
                                                             <React.Fragment key={ccName}>
                                                                 {/* Level 2: Cost Center Row */}
                                                                 <Tr 
                                                                     fontWeight="bold" 
                                                                     bg="white"
                                                                     onClick={() => toggleCostCenter(actName, ccName)}
                                                                     cursor="pointer"
                                                                     _hover={{ bg: "gray.50" }}
                                                                 >
                                                                     <Td style={{ paddingLeft: '24px' }}>
                                                                         <Flex align="center" gap={2}>
                                                                             {isCcExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                                                                             <Text>{ccName}</Text>
                                                                         </Flex>
                                                                     </Td>
                                                                     <Td textAlign="center">{ccTarget.est_bunch > 0 ? formatNumber(ccTarget.est_bunch) : '-'}</Td>
                                                                     <Td textAlign="right">{formatNumber(ccBunch)}</Td>
                                                                     <Td textAlign="center">{ccTarget.est_weight > 0 ? formatDecimal(ccTarget.est_weight) : '-'}</Td>
                                                                     <Td textAlign="right">{formatDecimal(ccWeight)}</Td>
                                                                     <Td textAlign="right">{formatDecimal(ccHk)}</Td>
                                                                     <Td textAlign="right">{formatDecimal(ccPremi)}</Td>
                                                                 </Tr>

                                                                 {isCcExpanded && groupedData[actName][ccName].map((loader) => (
                                                                     /* Level 3: Loader Row */
                                                                     <Tr key={loader.loader_id} _hover={{ bg: "gray.50" }}>
                                                                         <Td style={{ paddingLeft: '48px' }}>
                                                                             {loader.loader_name || '-'}
                                                                         </Td>
                                                                         <Td textAlign="center">-</Td>
                                                                         <Td textAlign="right">
                                                                             {formatNumber(loader.sum_bunch_count)}
                                                                         </Td>
                                                                         <Td textAlign="center">-</Td>
                                                                         <Td textAlign="right">
                                                                             {formatDecimal(loader.sum_real_weight)}
                                                                         </Td>
                                                                         <Td textAlign="right">
                                                                             {formatDecimal(loader.sum_hk)}
                                                                         </Td>
                                                                         <Td textAlign="right">
                                                                             {formatDecimal(loader.sum_premi_amount)}
                                                                         </Td>
                                                                     </Tr>
                                                                 ))}
                                                             </React.Fragment>
                                                         );
                                                     })}
                                                 </React.Fragment>
                                             );
                                         })}
                                     </Tbody>
                                 </Table>
                             </Box>
                         </Box>
                    )}
                </VStack>
            </Box>
        </Box>
    );
};

export default BkmMuatPage;
